import React, { useState, useDeferredValue, useEffect, useMemo } from 'react';
import { adminPanelService } from '../../../api/services/AdminPanelService';
import type { ListingStatsQuery } from '../../../api/services/AdminPanelService';
import type { ListingStatsModel } from '../../../api/types/admin';
import ListingStatsChartView, {
  metricLabels,
  type ListingStatsChartMetric,
} from './ListingStatsChart';

const METRICS: ListingStatsChartMetric[] = [
  'statsByStatuses',
  'statsByTopCategories',
  'statsBySecondaryCategories',
  'statsByProductTypes',
  'statsByCities',
  'statsByUsers',
];

type ListingStatsChartWrapperProps = {
  filterState: ListingStatsQuery;
  refreshTrigger?: number;
};

const ListingStatsChartWrapper: React.FC<ListingStatsChartWrapperProps> = ({ filterState, refreshTrigger }) => {
  const [selectedMetric, setSelectedMetric] = useState<ListingStatsChartMetric>('statsByStatuses');
  const deferredParams = useDeferredValue(filterState);
  const [stats, setStats] = useState<ListingStatsModel | null>(null);
  const [loading, setLoading] = useState(true);

  const paramsForFetch = useMemo(
    () => ({
      StartDate: deferredParams.StartDate ?? undefined,
      EndDate: deferredParams.EndDate ?? undefined,
      TopCategoryId: deferredParams.TopCategoryId ?? undefined,
      SecondaryCategoryId: deferredParams.SecondaryCategoryId ?? undefined,
      ProductTypeId: deferredParams.ProductTypeId ?? undefined,
      StatusId: deferredParams.StatusId ?? undefined,
      CityId: deferredParams.CityId ?? undefined,
      IsDeleted: deferredParams.IsDeleted ?? undefined,
    }),
    [deferredParams]
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminPanelService
      .getListingStats(paramsForFetch)
      .then((res) => {
        if (cancelled) return;
        setStats(res.isSuccess && res.data ? res.data : null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [paramsForFetch, refreshTrigger]);

  return (
    <div className="card h-100">
      <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
        <h6 className="mb-0">İlan İstatistikleri</h6>
        <select
          className="form-select form-select-sm"
          style={{ maxWidth: '100%', minWidth: '10rem' }}
          value={selectedMetric}
          onChange={(e) => setSelectedMetric(e.target.value as ListingStatsChartMetric)}
        >
          {METRICS.map((m) => (
            <option key={m} value={m}>
              {metricLabels[m]}
            </option>
          ))}
        </select>
      </div>
      <div className="card-body overflow-x-auto">
        {loading ? (
          <div className="d-flex align-items-center justify-content-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Yükleniyor...</span>
            </div>
          </div>
        ) : (
          <ListingStatsChartView stats={stats} metric={selectedMetric} />
        )}
      </div>
    </div>
  );
};

export default ListingStatsChartWrapper;
